import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { useVorgangLocking } from '@/features/vorgangLocking/useVorgangLocking';
import type { VorgangDetail } from '@/types';

export interface VorgangLockContextType {
  isLockedByCurrentUser: boolean;
  isLockedByOtherUser: boolean;
  lockedBy: string | undefined;
  lockVorgang: () => Promise<void>;
  unlockVorgang: () => Promise<void>;
  isLocking: boolean;
  isUnlocking: boolean;
}

export const VorgangLockContext = createContext<VorgangLockContextType | null>(
  null,
);

export interface VorgangLockProviderProps {
  children: React.ReactNode;
  vorgang: VorgangDetail | undefined;
  onLockSuccess?: () => void;
  onLockError?: (error: Error) => void;
  onUnlockSuccess?: () => void;
  onUnlockError?: (error: Error) => void;
  onLockStatusChange?: (isLockedByOther: boolean, lockedBy?: string) => void;
  refetch?: () => Promise<unknown>;
  autoLock?: boolean;
}

export const VorgangLockProvider: React.FC<VorgangLockProviderProps> = ({
  children,
  vorgang,
  onLockSuccess,
  onLockError,
  onUnlockSuccess,
  onUnlockError,
  onLockStatusChange,
  refetch,
  autoLock = true,
}) => {
  const { user } = useAuth();
  const currentUser = user?.email || '';
  const hasAttemptedLock = useRef(false);

  const lockingState = useVorgangLocking({
    vorgang,
    currentUser,
    onLockSuccess,
    onLockError,
    onUnlockSuccess,
    onUnlockError,
    onLockStatusChange,
    refetch,
    autoLock,
  });

  // Auto-lock on mount if enabled and vorgang is available
  useEffect(() => {
    if (
      autoLock &&
      vorgang?.id &&
      !lockingState.isLockedByCurrentUser &&
      !lockingState.isLockedByOtherUser &&
      !hasAttemptedLock.current
    ) {
      hasAttemptedLock.current = true;
      lockingState.lockVorgang();
    }
  }, [
    autoLock,
    vorgang?.id,
    lockingState.isLockedByCurrentUser,
    lockingState.isLockedByOtherUser,
  ]);

  // Reset the flag when vorgang changes
  useEffect(() => {
    hasAttemptedLock.current = false;
  }, [vorgang?.id]);

  return (
    <VorgangLockContext.Provider value={lockingState}>
      {children}
    </VorgangLockContext.Provider>
  );
};

export const useVorgangLockContext = () => {
  const context = useContext(VorgangLockContext);
  if (!context) {
    throw new Error(
      'useVorgangLockContext must be used within a VorgangLockProvider',
    );
  }
  return context;
};
